/**
 * Client-safe ownership gate for Blob URLs accepted as trusted application
 * storage. Keep this dependency-free: upload staging uses it in the browser.
 */

const BLOB_HOST = '0edx89zhjrasqnjf.public.blob.vercel-storage.com'
const CDN_HOST = 'cdn.brew.new'

/** Brew's CDN and Blob store origins, e.g. for a CSP that loads stored images. */
export const OWN_BLOB_ORIGINS = [`https://${CDN_HOST}`, `https://${BLOB_HOST}`]

/**
 * Parse the actual host. Accept only Brew's configured Blob store and CDN,
 * never an arbitrary `*.blob.vercel-storage.com` account.
 */
export function isOwnBlobHostUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') {
      return false
    }
    const host = parsed.hostname.toLowerCase()
    return host === CDN_HOST || host === BLOB_HOST
  } catch {
    return false
  }
}

/**
 * Only Brew's raw Blob STORE host (not the CDN mapping). Callers that
 * hostname-swap store URLs onto the CDN must use this — a suffix match on
 * `*.blob.vercel-storage.com` would rewrite other accounts' URLs into
 * guaranteed 404s.
 */
export function isOwnBlobStoreHostUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname.toLowerCase() === BLOB_HOST
    )
  } catch {
    return false
  }
}

/** Restrict a trusted Blob consumer to one application-owned path namespace. */
export function isOwnBlobUrlUnderPrefix(url: string, prefix: string): boolean {
  if (!isOwnBlobHostUrl(url)) {
    return false
  }
  const normalizedPrefix = prefix.replace(/^\/+|\/+$/g, '')
  if (!normalizedPrefix) {
    return false
  }
  try {
    return new URL(url).pathname.startsWith(`/${normalizedPrefix}/`)
  } catch {
    return false
  }
}

/**
 * The upload fast lane's staged originals (`UPLOAD_ORIGINALS_PREFIX` in
 * `lib/core/media/image-upload-support.ts`, pinned by
 * `tests/upload-url-lanes.test.ts`). Retention deletes them 24h after upload.
 */
const TRANSIENT_UPLOAD_PREFIX = 'uploads/originals'

/**
 * A Brew-hosted URL that will stop resolving: a staged original is an
 * internal handoff to the brand-import drainer, never content. Anything that
 * persists an image URL — email markup, a chat message, a library row —
 * must hold the durable `uploads/normalized/` asset instead.
 */
export function isTransientBrewUploadUrl(
  url: string | null | undefined
): boolean {
  return (
    typeof url === 'string' &&
    isOwnBlobUrlUnderPrefix(url, TRANSIENT_UPLOAD_PREFIX)
  )
}

/**
 * The keys Brew's image generator writes (`lib/core/media/image-gen/helpers.ts`
 * and the logo overlay): `generated-image-<epoch>…` at the store root, or
 * under `generated-image/`.
 */
const GENERATED_IMAGE_KEY = /^generated-image[-/]/

/**
 * An image Brew generated. It is "made with Brew" wherever a caller hands it
 * back (`add_image` with a `generate_image` URL, a chat attachment), so a
 * library write files it with the brand's generated images, never among the
 * images from the brand's site or its uploads.
 */
export function isBrewGeneratedImageUrl(
  url: string | null | undefined
): boolean {
  if (typeof url !== 'string' || !isOwnBlobHostUrl(url)) {
    return false
  }
  return GENERATED_IMAGE_KEY.test(new URL(url).pathname.replace(/^\/+/, ''))
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Both hosts, then everything up to a character no written URL can contain
 * (whitespace, `"`, a tag bracket, the `\` of a JSON-escaped quote, a
 * template literal's backtick). A filename keeps its `'` and `(…)` — the
 * store does not percent-encode them — so what closes a match is decided by
 * what opened it (`closeTransientUploadUrl`).
 */
const TRANSIENT_UPLOAD_URL_PATTERN = new RegExp(
  `https://(?:${escapeRegExp(CDN_HOST)}|${escapeRegExp(BLOB_HOST)})/${escapeRegExp(TRANSIENT_UPLOAD_PREFIX)}/[^\\s"<>\\\\\`]+`,
  'gi'
)

/** Entities rendered HTML writes for a quote inside an attribute value. */
const SINGLE_QUOTE_ENTITY = '&(?:apos|#0*39|#x0*27);'
const DOUBLE_QUOTE_ENTITY = '&(?:quot|#0*34|#x0*22);'
const OPENED_BY_SINGLE_QUOTE_ENTITY = new RegExp(`${SINGLE_QUOTE_ENTITY}$`, 'i')
const OPENED_BY_DOUBLE_QUOTE_ENTITY = new RegExp(`${DOUBLE_QUOTE_ENTITY}$`, 'i')
const SINGLE_QUOTE_ENTITIES = new RegExp(SINGLE_QUOTE_ENTITY, 'gi')
const DOUBLE_QUOTE_ENTITIES = new RegExp(DOUBLE_QUOTE_ENTITY, 'gi')
/** Enough text before a match to hold the longest opener (`&#x0027;`). */
const OPENER_LENGTH = 12

function cutAt(url: string, closer: RegExp | string): string {
  const index =
    typeof closer === 'string' ? url.indexOf(closer) : url.search(closer)
  return index === -1 ? url : url.slice(0, index)
}

/** A CSS `url(…)` or Markdown `(…)` ends at its first unbalanced `)`. */
function cutAtUnbalancedParen(url: string): string {
  let depth = 0
  for (let index = 0; index < url.length; index++) {
    if (url[index] === '(') {
      depth++
    } else if (url[index] === ')') {
      if (depth === 0) {
        return url.slice(0, index)
      }
      depth--
    }
  }
  return url
}

/**
 * Trim one match to the URL it is, by the text that opened it: a `'` (or its
 * entity, as React renders `url('…')` inside `style="…"`) closes at the next
 * one and `&quot;` at the next `&quot;`. A `"`-opened URL already stopped at
 * the pattern's own `"` — its `'` (rendered `&#x27;`) and `)` are part of it.
 * Anything else (`url(…)`, Markdown, text) closes at the first unbalanced
 * `)`, so `Screen Recording (1).gif` keeps its `(1)`.
 */
function closeTransientUploadUrl(opener: string, match: string): string {
  if (OPENED_BY_SINGLE_QUOTE_ENTITY.test(opener)) {
    return cutAt(match, new RegExp(SINGLE_QUOTE_ENTITY, 'i'))
  }
  if (OPENED_BY_DOUBLE_QUOTE_ENTITY.test(opener)) {
    return cutAt(match, new RegExp(DOUBLE_QUOTE_ENTITY, 'i'))
  }
  if (opener.endsWith("'")) {
    return cutAt(match, "'")
  }
  if (opener.endsWith('"')) {
    return match
  }
  return cutAtUnbalancedParen(match)
}

/**
 * Every transient Brew upload URL in a markup string (JSX or HTML), in first
 * occurrence order, exactly as written — so a caller can rewrite each one by
 * plain replacement. Decode one with `decodeMarkupUrl` before fetching it.
 */
export function findTransientBrewUploadUrls(markup: string): Array<string> {
  const matches = [...markup.matchAll(TRANSIENT_UPLOAD_URL_PATTERN)].map(
    (match) =>
      closeTransientUploadUrl(
        markup.slice(Math.max(0, match.index - OPENER_LENGTH), match.index),
        match[0]
      )
  )
  return [...new Set(matches)].filter(isTransientBrewUploadUrl)
}

/**
 * A URL as markup wrote it, as the store names it: rendered HTML escapes `&`
 * and quotes inside an attribute value (`&amp;`, `&#x27;`).
 */
export function decodeMarkupUrl(url: string): string {
  return url
    .replace(SINGLE_QUOTE_ENTITIES, "'")
    .replace(DOUBLE_QUOTE_ENTITIES, '"')
    .replaceAll('&amp;', '&')
}
